/*

  Mobile-Status.ts

  Mobile-only horizontal status component for Gáta Dagsins

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ts } from "./i18n";
import { ComponentFunc, m } from "./mithril";
import { IPlayerMove, IView } from "./types";
import { SunCorona } from "./suncorona";
import { RiddleScore } from "./riddlescore";
import { glyph } from "./util";

interface IMobileStatusAttributes {
  view: IView;
  selectedMoves: IPlayerMove[];
  bestMove: IPlayerMove | undefined;
  onMoveClick: (word: string, coord: string) => void;
  onStatsClick: () => void;
}

// Mobile-only horizontal status display
export const MobileStatus: ComponentFunc<IMobileStatusAttributes> = () => {
  return {
    view: (vnode) => {
      const { view, selectedMoves, bestMove, onMoveClick, onStatsClick } = vnode.attrs;
      const { riddle } = view.model;
      if (!riddle) return null;
      const { bestPossibleScore, globalBestScore, personalBestScore } = riddle;

      // Determine if player achieved best possible score
      const celebrate = bestMove && bestMove.word !== "";

      // Determine current leader score (may be this player or another)
      let leaderScore = 0;
      let isPlayerLeading = false;
      if (globalBestScore && globalBestScore.score > 0) {
        leaderScore = globalBestScore.score;
        // Check if player is leading
        isPlayerLeading = personalBestScore >= globalBestScore.score;
      } else {
        leaderScore = personalBestScore;
        isPlayerLeading = personalBestScore > 0;
      }

      return m(".mobile-status-container", [
        // Current word score (leftmost) - uses RiddleScore component in mobile mode
        m(".mobile-status-item.left",
          m(RiddleScore, { riddle, mode: "mobile" }),
        ),

        // Interactive card containing player best and leader scores
        m(".mobile-status-card", {
          onclick: onStatsClick,
          title: ts("Tölfræði og stigatafla")
        }, [
          // Player's best score
          m(".mobile-status-card-item.player-best", [
            m(".mobile-status-label", ts("Þín besta")),
            m(".mobile-status-score", personalBestScore.toString())
          ]),

          // Current leader score
          m(".mobile-status-card-item.leader" + (isPlayerLeading ? ".is-player" : ""), [
            m(".mobile-status-label", isPlayerLeading ? ts("Þú leiðir!") : ts("Best til þessa")),
            m(".mobile-status-score", leaderScore.toString())
          ]),

          // Chevron indicator (overlaid at bottom center)
          m(".mobile-status-card-icon", glyph("chevron-down"))
        ]),

        // Best possible score
        m(
          ".mobile-status-item.right.best-possible"
          + (celebrate ? ".celebrate" : ""),
          {
            onclick: () => celebrate && onMoveClick(bestMove.word, bestMove.coord)
          },
          [
            // Wrapper for score and corona to position them together
            m(".mobile-best-score-wrapper", [
              celebrate ? m(SunCorona, { animate: true, size: 100 }) : null,
              m(".mobile-status-score", bestPossibleScore.toString())
            ])
          ]
        )
      ]);
    }
  };
};
